import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Drawer, Typography, alpha } from "@mui/material";
import {
  EventNote as EventNoteIcon,
  MedicalServices as MedicalServicesIcon,
} from "@mui/icons-material";

import SectionCard from "../../../molecules/SectionCard";
import InfoRow from "../../../atoms/InfoRow";
import ActionButton from "../../../atoms/ActionButton";
import FormTextField from "../../../atoms/FormTextField";
import DrawerHeader from "../../../atoms/DrawerHeader";
import EmptyState from "../../../atoms/EmptyState";
import AppointmentBillingSection from "../../Billing/AppointmentBillingSection";
import { useClinicTheme } from "../../../../theme";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import { useStartExaminationMutation } from "../../../../store";
import useExaminationAction from "../../../../hooks/useExaminationAction";
import { useAlert } from "../../../../context/AlertProvider";
import { staffLabel } from "../../../../utils/staffName";
import useTerms from "../../../../hooks/useTerms";

const STATUS_META = {
  scheduled: { label: "Planlandı" },
  in_examination: { label: "Muayenede" },
  cancelled: { label: "İptal Edildi" },
  completed: { label: "Tamamlandı" },
  finished: { label: "Tamamlandı" },
  no_show: { label: "Gelmedi" },
  encounter: { label: "Görüşme" },
};

const TYPE_META = {
  ilk_muayene: "İlk Muayene",
  kontrol: "Kontrol",
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
};

const formatTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "—";
  }
};

/**
 * Randevu detay drawer'ı. Randevu görüntüleme + detay düzenleme (şikayet/not)
 * + muayene başlatma + silme (sadece iptal edilmiş randevular).
 *
 * @property {boolean} open
 * @property {() => void} onClose
 * @property {object|null} appointment
 * @property {(doctorId: string) => string} getDoctorName
 * @property {Array} labTests
 * @property {string} [subtitle]
 * @property {boolean} [canEdit]
 * @property {(payload: { appointmentId, complaint, notes }) => void} [onSave]
 * @property {(() => void) | null} [onDelete]   Only shown when appointment is cancelled
 * @property {string} token
 */
export default function AppointmentDetailDrawer({
  open,
  onClose,
  appointment,
  getDoctorName,
  labTests,
  subtitle,
  canEdit = true,
  onSave,
  onDelete,
  token,
}) {
  const terms = useTerms();
  const navigate = useNavigate();
  const { palette } = useClinicTheme();
  const [startExamination, { isLoading: isStartingExamination }] =
    useStartExaminationMutation();
  const examAction = useExaminationAction(appointment);
  const { showError } = useAlert();

  const [form, setForm] = useState({ complaint: "", notes: "" });

  useEffect(() => {
    if (!open || !appointment) return;
    setForm({
      complaint: appointment.complaint || "",
      notes: appointment.notes || "",
    });
  }, [open, appointment]);

  const handleMuayene = async () => {
    if (!appointment?._id) return;
    try {
      if (appointment.status === "scheduled") {
        await startExamination({
          token,
          appointmentId: appointment._id,
        }).unwrap();
      }
      onClose?.();
      navigate(`/Randevular/${appointment._id}/Muayene`);
    } catch (err) {
      const msg =
        err?.data?.message || err?.error || `${terms.exam} başlatılamadı`;
      showError(msg);
    }
  };

  const handleSaveClick = () => {
    if (!appointment?._id) return;
    onSave?.({
      appointmentId: appointment._id,
      complaint: form.complaint,
      notes: form.notes,
    });
  };

  if (!appointment) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 520 } },
        }}
      >
        <DrawerHeader
          icon={<EventNoteIcon />}
          title="Randevu Detayı"
          onClose={onClose}
        />
        <EmptyState variant="inline" icon={<EventNoteIcon />} title="Randevu seçilmedi." />
      </Drawer>
    );
  }

  const a = appointment;
  const statusKey = String(a.status || "").toLowerCase();
  const isCancelled = statusKey === "cancelled";
  const statusLabel =
    (statusKey === "in_examination" ? terms.inExam : STATUS_META[statusKey]?.label) || a.status || "Bilinmiyor";
  const typeLabel =
    ({ ...TYPE_META, ilk_muayene: terms.firstVisitTitle })[String(a.appointmentType || "").toLowerCase()] || "Randevu";

  const doctorId = typeof a.doctor === "object" ? a.doctor?._id : a.doctor;
  const doctorName = doctorId
    ? getDoctorName?.(doctorId) || staffLabel({ doctorRoleName: a?.snapshot?.doctorRoleName, name: a?.snapshot?.doctorName }) || "—"
    : staffLabel({ doctorRoleName: a?.snapshot?.doctorRoleName, name: a?.snapshot?.doctorName }) || "—";

  const dateStr = formatDate(a.startAt);
  const startStr = formatTime(a.startAt);
  const endStr = a.endAt ? formatTime(a.endAt) : null;
  const timeStr = endStr ? `${startStr} - ${endStr}` : startStr;

  const testNames = (a.labTestIds || [])
    .map((id) => {
      const test = (labTests || []).find(
        (t) => String(t._id) === String(id)
      );
      return test?.name || null;
    })
    .filter(Boolean);

  const muayeneLabel =
    examAction.mode === "start" && isStartingExamination ? "Başlatılıyor..." : examAction.label;
  const muayeneVariant = examAction.variant;

  const updateField = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<EventNoteIcon sx={{ fontSize: 24 }} />}
        title="Randevu Detayı"
        subtitle={subtitle || dateStr}
        onClose={onClose}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          bgcolor: COLORS.slate50,
        }}
      >
        <SectionCard title="Genel Bilgiler">
          <InfoRow label="Tarih">
            <Typography sx={{ fontWeight: 700, color: COLORS.slate800, fontSize: TYPO.body }}>
              {dateStr}
            </Typography>
          </InfoRow>
          <InfoRow label="Saat">
            <Typography sx={{ fontWeight: 700, color: COLORS.slate800, fontSize: TYPO.body }}>
              {timeStr}
            </Typography>
          </InfoRow>
          <InfoRow label={terms.doctor}>
            <Typography sx={{ fontWeight: 700, color: COLORS.slate800, fontSize: TYPO.body }}>
              {doctorName}
            </Typography>
          </InfoRow>
          <InfoRow label="Tür">
            <Chip
              label={typeLabel}
              size="small"
              sx={{
                height: 22,
                fontSize: TYPO.caption,
                borderRadius: "999px",
                bgcolor: alpha(palette.primary, 0.08),
                color: palette.primary,
                fontWeight: 700,
              }}
            />
          </InfoRow>
          <InfoRow label="Durum" noBorder>
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                height: 22,
                fontSize: TYPO.caption,
                borderRadius: "999px",
                bgcolor: isCancelled
                  ? alpha(COLORS.error, 0.08)
                  : alpha(COLORS.success, 0.08),
                color: isCancelled ? COLORS.errorDark : COLORS.successDark,
                fontWeight: 700,
              }}
            />
          </InfoRow>
        </SectionCard>

        {(muayeneLabel || examAction.note) && (
          <SectionCard title={terms.exam}>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <MedicalServicesIcon
                sx={{ fontSize: 20, color: palette.primary }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontWeight: 700,
                  color: COLORS.slate700,
                  fontSize: TYPO.body,
                }}
              >
                {muayeneLabel ? `Bu randevu için ${terms.examLower} açılabilir.` : examAction.note}
              </Typography>
              {muayeneLabel ? (
                <ActionButton
                  variant={muayeneVariant}
                  palette={palette}
                  label={muayeneLabel}
                  compact
                  loading={isStartingExamination}
                  onClick={handleMuayene}
                  aria-label={muayeneLabel}
                />
              ) : null}
            </Box>
          </SectionCard>
        )}

        {/* Hizmet / paket (billing modülü; kapalıysa çizilmez) */}
        {canEdit || appointment?.serviceName ? <AppointmentBillingSection appointment={appointment} /> : null}

        <SectionCard title="Şikayet ve Not">
          <InfoRow label="Şikayet">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              placeholder={`${terms.patient} şikayeti`}
              value={form.complaint}
              onChange={(e) => updateField({ complaint: e.target.value })}
              disabled={!canEdit}
              inputProps={{ "aria-label": "Randevu şikayeti" }}
            />
          </InfoRow>
          <InfoRow label="Not" noBorder>
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              rows={3}
              placeholder="Randevu notu"
              value={form.notes}
              onChange={(e) => updateField({ notes: e.target.value })}
              disabled={!canEdit}
              inputProps={{ "aria-label": "Randevu notu" }}
            />
          </InfoRow>
        </SectionCard>

        {testNames.length > 0 && (
          <SectionCard title="Tahliller">
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {testNames.map((name, i) => (
                <Chip
                  key={i}
                  label={name}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    bgcolor: alpha(palette.primary, 0.08),
                    color: palette.primary,
                    borderRadius: "999px",
                  }}
                />
              ))}
            </Box>
          </SectionCard>
        )}

        {isCancelled && (a.cancellation?.reason || a.cancellation?.cancelledAt) && (
          <SectionCard title="İptal Bilgileri">
            {a.cancellation?.reason && (
              <InfoRow label="Neden">
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: COLORS.errorDark,
                    fontSize: TYPO.body,
                  }}
                >
                  {a.cancellation.reason}
                </Typography>
              </InfoRow>
            )}
            {a.cancellation?.cancelledAt && (
              <InfoRow label="Tarih" noBorder>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: COLORS.slate700,
                    fontSize: TYPO.body,
                  }}
                >
                  {new Date(a.cancellation.cancelledAt).toLocaleString(
                    "tr-TR",
                    {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </Typography>
              </InfoRow>
            )}
          </SectionCard>
        )}
      </Box>

      {/* Footer — inline, 3 slots possible (cancel / delete / save) */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: COLORS.white,
          borderTop: `1px solid ${COLORS.slate200}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onClose}
          variant="text"
          aria-label="İptal"
          sx={{
            fontWeight: 800,
            textTransform: "none",
            color: COLORS.slate600,
            px: 3,
            borderRadius: 2,
            "&:hover": { bgcolor: COLORS.slate100 },
          }}
        >
          Kapat
        </Button>
        {onDelete && (
          <ActionButton
            variant="delete"
            palette={palette}
            onClick={onDelete}
            aria-label="Randevuyu sil"
            label="Sil"
          />
        )}
        {canEdit && onSave && (
          <ActionButton
            variant="save"
            palette={palette}
            onClick={handleSaveClick}
            aria-label="Kaydet"
            label="Kaydet"
          />
        )}
      </Box>
    </Drawer>
  );
}
